import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2, Clock, Wrench, ImageIcon, Loader2, AlertCircle,
} from "lucide-react";

const STATUS_CFG = {
  aguardando_execucao: { label: "Aguardando execução", cor: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  em_execucao: { label: "Em execução", cor: "bg-blue-100 text-blue-800 border-blue-300", icon: Wrench },
  concluida: { label: "Concluída", cor: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  cancelada: { label: "Cancelada", cor: "bg-red-100 text-red-700 border-red-300", icon: AlertCircle },
};

const fmtDt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

export default function PublicOSStatus() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    setLoading(true);
    axios.get(`${BACKEND}/api/public/os/${token}/status`)
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "OS não encontrada" : "Erro ao carregar"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="p-8 max-w-md text-center" data-testid="erro-os-publica">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-medium text-slate-900 mb-2">{err}</h1>
          <p className="text-sm text-slate-500">
            Verifique o link ou QR Code. Esta página exibe o acompanhamento público de Ordens de Serviço.
          </p>
        </Card>
      </div>
    );
  }

  const cfg = STATUS_CFG[d?.status] || STATUS_CFG.aguardando_execucao;
  const StatusIcon = cfg.icon;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="public-os-page">
        {/* Header LAB */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-light">
            <span className="text-orange-600">L</span>
            <span className="text-slate-900">AB</span>
          </h1>
          <p className="text-xs text-slate-500 tracking-widest uppercase">Locar Ativo Brasil — Pós-venda</p>
        </div>

        {/* Card principal */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Ordem de Serviço</div>
              <div className="text-2xl font-mono font-medium" data-testid="os-numero">{d.numero}</div>
            </div>
            <Badge className={`${cfg.cor} border px-3 py-1.5 flex items-center gap-1.5`}>
              <StatusIcon className="w-3.5 h-3.5" />
              <span className="text-xs font-mono">{cfg.label}</span>
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Cliente</div>
              <div className="font-medium">{d.cliente_nome || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Equipamento</div>
              <div>{d.equipamento_label || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Técnico designado</div>
              <div data-testid="os-tecnico">{d.tecnico_nome || "A definir"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Execução prevista</div>
              <div>{d.data_execucao ? new Date(d.data_execucao).toLocaleDateString("pt-BR") : "A definir"}</div>
            </div>
          </div>

          {d.observacoes_tecnicas && (
            <div className="mt-4 p-3 bg-slate-50 rounded text-xs">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-1">Observações</div>
              {d.observacoes_tecnicas}
            </div>
          )}
        </Card>

        {/* Timeline */}
        {d.timeline && d.timeline.length > 0 && (
          <Card className="p-5">
            <h2 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-600" /> Linha do tempo
            </h2>
            <div className="space-y-2.5">
              {d.timeline.map((t, idx) => (
                <div key={idx} className="flex items-start gap-3 text-xs border-l-2 border-orange-200 pl-3"
                  data-testid={`timeline-${idx}`}>
                  <div className="font-mono text-slate-500 w-32 flex-shrink-0">{fmtDt(t.data)}</div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">
                      {t.status_anterior} → {t.status_novo}
                    </div>
                    {t.observacao && <div className="text-slate-600">{t.observacao}</div>}
                    {t.user_nome && <div className="text-[10px] text-slate-400">por {t.user_nome}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Fotos */}
        {d.fotos && d.fotos.length > 0 && (
          <Card className="p-5">
            <h2 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-orange-600" /> Fotos da execução ({d.fotos.length})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {d.fotos.map((f, idx) => (
                <a key={f.id || idx} href={`${BACKEND}${f.url}`} target="_blank" rel="noreferrer"
                  className="block aspect-square bg-slate-100 rounded overflow-hidden hover:shadow-lg transition"
                  data-testid={`foto-${idx}`}>
                  <img src={`${BACKEND}${f.url}`} alt={f.legenda || "Foto"} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </Card>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-6">
          Esta página é atualizada em tempo real. Para questões urgentes, fale com nosso suporte.
        </p>
      </div>
    </div>
  );
}
